import parse from "html-react-parser";
import React, { createContext, useContext, useEffect, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useRevalidator, useRouteLoaderData } from "react-router-dom";

import { useRequest } from "@components/AppHook";
import { setTmr, tmrPushEvent } from "@components/Counters";
import { ImageCropperModalProvider } from "@components/Fields/ImageCropper";
import { ToastAlert } from "@components/Modals";
import { NavigationBar, useDesktopVersion } from "@components/NaviHeader";
import { BackendPromise, buildQS, parseQS, url_reverse } from "@components/Utils";
import { getGemHost } from "@vkplay/shared";

const AppContext = createContext({});

export const useAppContext = () => useContext(AppContext);

export const useAppApi = (url) => {
    const { apiData, setApiData, setApiIsLoading, apiUrl, setApiUrl, apiIsLoading, apiReget, setApiReget } =
        useContext(AppContext);
    const location = useLocation();
    const { api_get } = useRequest();

    const getApi = () => {
        setApiUrl(null); // обнуляем урл для правильной работы спиннеров
        setApiIsLoading(true);
        api_get(null, url + "?" + buildQS(parseQS()), {
            then: (resp) => {
                setApiData(resp);
                setApiUrl(url);
                setApiReget(false);
            },
        });
    };

    useEffect(() => {
        getApi();
    }, [location]);

    useEffect(() => {
        if (apiReget === true) {
            getApi();
        }
    }, [apiReget]);

    if (url !== apiUrl) {
        return { undefined, setApiData, apiUrl, apiIsLoading };
    }
    return { apiData, setApiData, apiUrl, apiIsLoading, setApiReget };
};

export function AppLayout() {
    const { t } = useTranslation();
    const [apiUrl, setApiUrl] = useState(null);
    const [apiData, setApiData] = useState(undefined);
    const [apiIsLoading, setApiIsLoading] = useState(false);
    const [delData, setDelData] = useState({});
    const [toastMessage, setToastMessage] = useState("");
    const [apiReget, setApiReget] = useState(false);
    const [showMobileAlert, setShowMobileAlert] = useState(true);
    const revalidator = useRevalidator();
    const commonInfo = useRouteLoaderData("commonInfo");
    const { showDesktopVersionLink } = useDesktopVersion();

    const mobileNoticeClose = () => {
        localStorage.setItem("devarea_mobile_notice_closed", true);
        setShowMobileAlert(false);
    };

    useEffect(() => {
        if (localStorage.getItem("devarea_mobile_notice_closed")) {
            setShowMobileAlert(false);
        }
    }, []);

    useEffect(() => {
        const login_page = `https://${getGemHost("account")}/login/`;
        if (commonInfo.errcode === "need_auth") {
            window.location = login_page + "?continue=" + encodeURIComponent(window.location.href);
        } else if (commonInfo.errcode === "steam_account") {
            window.location = login_page + "?continue=" + encodeURIComponent(window.location.href);
        } else if (commonInfo.errcode === "need_tou_accept") {
            const tou_accept = url_reverse("tou_accept");
            if (window.location.pathname !== tou_accept) {
                window.location = tou_accept;
            }
        } else if (commonInfo.errcode === "need_registration") {
            const register = url_reverse("devs:register");
            const reject = url_reverse("devs:reject");
            if (![register, reject].includes(window.location.pathname)) {
                window.location = register;
            }
        }

        const user_id = commonInfo?.auth_info?.user_id;
        if (user_id) {
            setTmr(user_id);
        }

        const login = commonInfo?.account?.login;
        if (login) {
            tmrPushEvent(
                "user_type",
                encodeURIComponent(commonInfo.account.is_super ? "Суперпользователи" : "Разработчики")
            );
        }

        if (!window.CSRF_JWT_TOKEN) {
            BackendPromise.ext_api_get("/social/profile/v2/session").then((resp) => {
                if (resp.status === "ok") {
                    window.CSRF_JWT_TOKEN = resp.token;
                }
            });
        }
    }, [JSON.stringify(commonInfo)]);

    if (!commonInfo.auth_info) return;

    const noticeClose = (id) => {
        BackendPromise.post(url_reverse("api:notice:close", { notice_id: id })).then((resp) => {
            if (resp.status === "ok") {
                localStorage.setItem("devarea_notice_" + id, true);
                revalidator.revalidate();
            }
        });
    };

    return (
        <ImageCropperModalProvider apiData={apiData} setApiData={setApiData}>
            <AppContext.Provider
                value={{
                    delData,
                    setDelData,
                    toastMessage,
                    setToastMessage,
                    apiData,
                    setApiData,
                    apiIsLoading,
                    setApiIsLoading,
                    apiUrl,
                    setApiUrl,
                    apiReget,
                    setApiReget,
                }}
            >
                <NavigationBar commonInfo={commonInfo} />

                <ToastAlert />

                {showMobileAlert && (
                    <Alert variant="info" className="d-block d-md-none" onClose={mobileNoticeClose} dismissible>
                        {t("Часть функций кабинета доступна только в ПК-версии.")} {showDesktopVersionLink(true)}
                    </Alert>
                )}

                <Container fluid className="my-3">
                    <Row>
                        <Col>
                            {commonInfo.devarea_notice?.map((notice, i) => {
                                if (localStorage.getItem("devarea_notice_" + notice.id) || notice.text === "") {
                                    return null;
                                }
                                return (
                                    <Alert
                                        key={`devarea_notice_alert${i}`}
                                        variant="warning"
                                        onClose={() => noticeClose(notice.id)}
                                        dismissible
                                    >
                                        <Row>
                                            <Col key={`devarea_notice${i}`}>{parse(notice.text)}</Col>
                                        </Row>
                                    </Alert>
                                );
                            })}
                        </Col>
                    </Row>
                    <Outlet />
                </Container>
                {commonInfo?.devel_env && (
                    <div id="devel_warning_bar" className={commonInfo?.devel_env.type}>
                        {commonInfo?.devel_env.host}
                    </div>
                )}
            </AppContext.Provider>
        </ImageCropperModalProvider>
    );
}

export const commonInfoLoader = async () => await BackendPromise.get(url_reverse("api:common_info"));
